/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { createTheme, adaptV4Theme } from "@mui/material";

export const Breakpoints = {
  xs: 0,
  sm: 560,
  md: 930,
  lg: 1280,
  xl: 1850,
};

const baseTheme = {
  typography: {
    fontFamily: "roboto",
  },
  breakpoints: {
    values: Breakpoints,
  },
};

// Light Theme
// export const lightTheme = createTheme(
//   adaptV4Theme({
//     ...baseTheme,
//     palette: {
//       mode: "light",
//       common: {
//         black: "#000000",
//         white: "#FFFFFF"
//       },
//       primary: {
//         light: "#FCFCFD",         // Light grey background
//         main: "#FCFCFD",          // Soft grey buttons, etc.
//         dark: "#A0A0A0",          // For hover/pressed states
//         contrastText: "#1A1A1A"
//       },
//       secondary: {
//         light: "#FCFCFD",         // Light neutral tone
//         main: "#C2C2C2",          // Accent elements
//         dark: "#8A8A8A",
//         contrastText: "#1A1A1A",
//         hover: {
//           main: "#B0B0B0"
//         }
//       },
//       tertiary: {
//         main: "#EAEAEA"           // Card/section backgrounds
//       },
//       text: {
//         color: "#1A1A1A"          // Standard dark text
//       },
//       secondaryText: {
//         color: "#6E6E6E"          // Muted/secondary text
//       },
//       hoverText: {
//         hover: {
//           color: "#333333"        // Slightly darker hover text
//         }
//       },
//       border: {
//         color: "#E0E0E0"          // Soft border for dividers
//       },
//       black: {
//         light: "#3A3A3A",
//         main: "#1F1F1F"
//       },
//       white: {
//         light: "#FAFAFA",
//         main: "#FFFFFF"
//       }
//     }
//   })
// );

export const lightTheme = createTheme(
  adaptV4Theme({
    ...baseTheme,
    palette: {
      mode: "light",
      common: {
        black: "#000000",
        white: "#FFFFFF",
      },
      primary: {
        main: "#FBFBFB",
      },
      secondary: {
        main: "#FBFBFB",
      },
      tertiary: {
        main: "#EAEAEA", // Card/section backgrounds
      },
      canvas: {
        main: "#EBEBEB", // Sidebar
      },
      background: {
        default: "#F8FAFC",
        paper: "#FFFFFF",
      },
      text: {
        primary: "#1A1A1A",
        secondary: "#6E6E6E",
        disabled: "#8A8A8A",
        color: "#1A1A1A", // Legacy alias
      },
      secondaryText: {
        color: "#6E6E6E", // Muted/secondary text
      },
      boxShadow: "2px 1px 2px rgba(0, 0, 0, 0.1)",
      hoverText: {
        hover: {
          color: "#333333", // Slightly darker hover text
        },
      },
      border: {
        color: "#E1E1E4", // Soft border for dividers
      },
      tableHeader: {
        color: '#FAFAFA'
      },
      black: {
        light: "#3A3A3A",
        main: "#1F1F1F",
      },
      white: {
        light: "#FAFAFA",
        main: "#FFFFFF",
      },
    },
  })
);

// Dark Theme
export const darkTheme = createTheme(
  adaptV4Theme({
    ...baseTheme,
    palette: {
      mode: "dark", // Specifies dark mode
      common: {
        black: "#000",
        white: "#fff",
      },
      primary: {
        light: "#212121",
        main: "#000000",
        dark: "#0b2941",
        contrastText: "#ffffff",
      },
      secondary: {
        light: "#424242",
        main: "#000000",
        dark: "#1565c0",
        contrastText: "#fff",
        hover: {
          main: "#0d47a1",
        },
      },
      tertiary: {
        main: "#313131",
      },
      canvas: {
        main: "#000000", // Sidebar
      },
      background: {
        default: "#0B1220",
        paper: "#121827",
      },
      text: {
        primary: "#FFFFFF",
        secondary: "#D0D1D2",
        disabled: "#9AA0B8",
        color: "#FFFFFF",
      },
      secondaryText: {
        color: "#D0D1D2",
      },
      border: {
        color: "#2B2B2E",
      },
      tableHeader: {
        color: "#1B1B1C"
      },
      hoverText: {
        hover: {
          color: "#FFFFFF",
        },
      },
      black: {
        light: "#424242",
        main: "#202020",
      },
      white: {
        light: "#e0e0e0",
        main: "#bdbdbd",
      },
    },
  })
);

export const VecrosTheme = createTheme(
  adaptV4Theme({
    palette: {
      common: {
        black: "#000",
        white: "#fff",
      },
      primary: {
        // light: '#fdfcea',
        light: "#fff",
        // main: '#002126',
        // main: '#0B6CFF',
        main: "#2196f3",
        dark: "#242424",
        contrastText: "#ffffff",
      },
      secondary: {
        light: "#fdfcea",
        main: "#005662",
        dark: "#3b3b3b",
        contrastText: "#fff",
        hover: {
          main: "#012a30",
        },
      },
      typography: {
        fontFamily: "roboto",
        h1: {},
      },
      black: {
        light: "#282828",
        main: "#202020",
      },
      white: {
        light: "#f1f1f1",
        main: "#fff",
      },
    },
    breakpoints: {
      values: Breakpoints,
    },
  })
);

// ---------- ADDING THESE CUSTOMIZED VALUES AS THE DEFAULT VALUES IN THE MATERIAL UI ARE NOT RESPONSIVE ----------

VecrosTheme.typography.h1 = {
  ...VecrosTheme.typography.h1,
  [VecrosTheme.breakpoints.up("xs")]: {
    fontSize: VecrosTheme.spacing(9),
  },
  [VecrosTheme.breakpoints.up("sm")]: {
    fontSize: VecrosTheme.spacing(10),
  },
  [VecrosTheme.breakpoints.up("md")]: {
    fontSize: VecrosTheme.spacing(11),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(12),
  },
  [VecrosTheme.breakpoints.up("xl")]: {
    fontSize: VecrosTheme.spacing(12.5),
  },
};

//VERIFIED BY USING IN A COMPONENT
VecrosTheme.typography.h2 = {
  ...VecrosTheme.typography.h2,
  [VecrosTheme.breakpoints.up("xs")]: {
    fontSize: VecrosTheme.spacing(5.5),
  },
  [VecrosTheme.breakpoints.up("sm")]: {
    fontSize: VecrosTheme.spacing(6),
  },
  [VecrosTheme.breakpoints.up("md")]: {
    fontSize: VecrosTheme.spacing(6.75),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(7.5),
  },
  [VecrosTheme.breakpoints.up("xl")]: {
    fontSize: VecrosTheme.spacing(8),
  },
};

VecrosTheme.typography.h3 = {
  ...VecrosTheme.typography.h3,
  [VecrosTheme.breakpoints.up("xs")]: {
    fontSize: VecrosTheme.spacing(3.75),
  },
  [VecrosTheme.breakpoints.up("sm")]: {
    fontSize: VecrosTheme.spacing(4.25),
  },
  [VecrosTheme.breakpoints.up("md")]: {
    fontSize: VecrosTheme.spacing(4.6),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(5.25),
  },
  [VecrosTheme.breakpoints.up("xl")]: {
    fontSize: VecrosTheme.spacing(6.5),
  },
};

VecrosTheme.typography.h4 = {
  ...VecrosTheme.typography.h4,
  [VecrosTheme.breakpoints.up("xs")]: {
    fontSize: VecrosTheme.spacing(3.3),
  },
  [VecrosTheme.breakpoints.up("sm")]: {
    fontSize: VecrosTheme.spacing(3.65),
  },
  [VecrosTheme.breakpoints.up("md")]: {
    fontSize: VecrosTheme.spacing(4),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(4.25),
  },
  [VecrosTheme.breakpoints.up("xl")]: {
    fontSize: VecrosTheme.spacing(4.5),
  },
};

VecrosTheme.typography.h5 = {
  ...VecrosTheme.typography.h5,
  [VecrosTheme.breakpoints.up("xs")]: {
    fontSize: VecrosTheme.spacing(2),
  },
  [VecrosTheme.breakpoints.up("sm")]: {
    fontSize: VecrosTheme.spacing(2.25),
  },
  [VecrosTheme.breakpoints.up("md")]: {
    fontSize: VecrosTheme.spacing(2.8),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(3.2),
  },
  [VecrosTheme.breakpoints.up("xl")]: {
    fontSize: VecrosTheme.spacing(3.3),
  },
};

//VERIFIED BY USING IN A COMPONENT
VecrosTheme.typography.h6 = {
  ...VecrosTheme.typography.h6,
  [VecrosTheme.breakpoints.up("xs")]: {
    fontSize: VecrosTheme.spacing(2),
  },
  [VecrosTheme.breakpoints.up("sm")]: {
    fontSize: VecrosTheme.spacing(2.15),
  },
  [VecrosTheme.breakpoints.up("md")]: {
    fontSize: VecrosTheme.spacing(2.3),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(2.5),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(2.8),
  },
};

//VERIFIED BY USING IN A COMPONENT
VecrosTheme.typography.body1 = {
  ...VecrosTheme.typography.body1,
  [VecrosTheme.breakpoints.up("xs")]: {
    fontSize: VecrosTheme.spacing(1.65),
  },
  [VecrosTheme.breakpoints.up("sm")]: {
    fontSize: VecrosTheme.spacing(1.75),
  },
  [VecrosTheme.breakpoints.up("md")]: {
    fontSize: VecrosTheme.spacing(1.9),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(2),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(2.2),
  },
};

//VERIFIED BY USING IN A COMPONENT
VecrosTheme.typography.body2 = {
  ...VecrosTheme.typography.body2,
  [VecrosTheme.breakpoints.up("xs")]: {
    fontSize: VecrosTheme.spacing(1.55),
  },
  [VecrosTheme.breakpoints.up("sm")]: {
    fontSize: VecrosTheme.spacing(1.65),
  },
  [VecrosTheme.breakpoints.up("md")]: {
    fontSize: VecrosTheme.spacing(1.7),
  },
  [VecrosTheme.breakpoints.up("lg")]: {
    fontSize: VecrosTheme.spacing(1.75),
  },
  [VecrosTheme.breakpoints.up("xl")]: {
    fontSize: VecrosTheme.spacing(1.8),
  },
};
